import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Button } from './ui/button';
import { ArrowRight, Layers, Shield, Zap } from 'lucide-react';

export const HeroSection = () => {
  const { language } = useLanguage();

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center pt-20 overflow-hidden theme-transition"
      data-testid="hero-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 bg-slate-50 dark:bg-slate-950">
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234338ca' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-indigo-500/10 dark:bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-violet-500/10 dark:bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-6 animate-fade-in">
              <Shield className="w-4 h-4" strokeWidth={1.5} />
              <span>{translations.hero.badge[language]}</span>
            </div>

            {/* Heading */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6 animate-slide-up"
              data-testid="hero-title"
            >
              {translations.hero.title[language]}
            </h1>

            {/* Subtitle */}
            <p 
              className="text-lg lg:text-xl text-slate-600 dark:text-slate-400 mb-8 max-w-xl animate-slide-up"
              style={{ animationDelay: '0.1s' }}
              data-testid="hero-subtitle"
            >
              {translations.hero.subtitle[language]}
            </p>

            {/* CTA */}
            <div 
              className="flex flex-wrap gap-4 animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              <Button
                onClick={scrollToContact}
                className="btn-primary flex items-center gap-2"
                data-testid="hero-cta-button"
              >
                {translations.hero.cta[language]}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>

            {/* Stats */}
            <div 
              className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-slate-200 dark:border-slate-800 animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            >
              <div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">50+</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {translations.hero.stats.projects[language]}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">99.9%</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {translations.hero.stats.uptime[language]}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">7/24</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {translations.hero.stats.support[language]}
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Abstract Visual */}
          <div className="relative hidden lg:block">
            <div className="relative w-full aspect-square">
              {/* Main Card */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] aspect-square">
                <div className="w-full h-full rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-600 shadow-2xl shadow-indigo-500/30 p-8 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                      <Layers className="w-8 h-8 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-white/30" />
                      <div className="w-3 h-3 rounded-full bg-white/30" />
                      <div className="w-3 h-3 rounded-full bg-white/30" />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/80 text-sm mb-2">
                      {language === 'tr' ? 'Proje Durumu' : 'Project Status'}
                    </div>
                    <div className="text-white text-2xl font-bold">
                      {language === 'tr' ? 'Geliştiriliyor' : 'In Progress'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Cards */}
              <div className="absolute top-0 right-0 w-32 h-32 rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-4 flex flex-col justify-between border border-slate-200 dark:border-slate-700">
                <Zap className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {language === 'tr' ? 'Teslimat' : 'Delivery'}
                  </div>
                  <div className="text-lg font-bold text-slate-900 dark:text-white">
                    {language === 'tr' ? 'Hızlı' : 'Fast'}
                  </div>
                </div>
              </div>

              <div className="absolute bottom-8 left-0 w-40 h-24 rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-4 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {language === 'tr' ? 'Müşteri' : 'Client'}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                      {language === 'tr' ? 'Memnuniyeti' : 'Satisfaction'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
